import React, { useState, useEffect, useRef, useContext, useMemo } from 'react'
import gql from "graphql-tag"
import { MentionsInput, Mention } from 'react-mentions'
import { useQuery } from '@apollo/client'
import { Link, useSearchParams } from "react-router-dom"
import ReactMarkdown from "react-markdown"
import { Divider } from "@material-ui/core"
import { Nav, NavItem, NavLink, TabContent, TabPane, Form, Input, FormGroup, Button, Label, Card, CardBody, CardText, Row, Col, Modal, ModalHeader, ModalBody } from "reactstrap"
import Select from 'react-select'
import Skeleton from "react-loading-skeleton"
import moment from 'moment-timezone'
import RepAvatar from '../Material/RepAvatar'
import { TaskQuery, AllTaskCommentsQuery, TaskStatusOptionsQuery } from "./Queries"
import { CreateTaskCommentMutation, CreateTaskChecklistMutation, UpdateTaskChecklistMutation, UpdateTaskMutation } from "./Mutations"
import withApolloClient from "../withApolloClient"
import "./TaskDetailsSidePanel.css"
import DatetimeRenderer from "../Material/DatetimeRenderer"
import TaskArchievePopover from './TaskComponents/TaskArchievePopover'
import EditCommentPopover from './TaskComponents/EditCommentPopover'
import ReactionsPopup from "./TaskComponents/ReactionsPopup"
import DocumentViewer from '../Material/DocumentViewer'
import HistoryList from '../Material/HistoryList'
import { RepsTypeAhead } from './TaskComponents/TypeAheadComponents'
import { CurrentUserContext } from '../Context/CurrentUserContext'
import { ForeignKeyInput, renderCustomColumn } from "./TaskComponents/ForeignKeyComponents"


const UpdateTaskCommentMutation = gql`
  mutation UpdateTaskComment($input: UpdateTaskCommentMutationInput!) {
    updateTaskComment(input: $input) {
      ok
      errors {
        messages
      }
    }
  }
`

function TaskDetailsSidePanel({ ...props }) {
    const { currentUser } = useContext(CurrentUserContext)
    const [searchParams, setSearchParams] = useSearchParams()
    const taskId = searchParams.get('task_id')
    const [activeTab, setActiveTab] = useState("#Updates")
    const [newChecklistTitle, setNewChecklistTitle] = useState("")
    const [panelError, setPanelError] = useState("")

    const { loading: taskLoading, error, data, refetch: refetchTaskQuery } = useQuery(TaskQuery, {
        variables: { id: taskId },
        fetchPolicy: 'cache-and-network',
        skip: !taskId, // Skip the query if taskId is not available
    })
    const task = data?.task
    const boardDirectUsers = useMemo(() => {
        const allUsers = task?.board?.allowedUsers?.edges?.map(({ node }) => node).filter(Boolean) || []
        const seen = new Set()
        return allUsers.filter((user) => {
            if (!user?.id || seen.has(user.id)) return false
            seen.add(user.id)
            return true
        })
    }, [task?.board])
    const assignableUserOptions = useMemo(() => {
        const allUsers = [
            ...boardDirectUsers,
            ...(task?.assignedTo?.edges?.map(({ node }) => node).filter(Boolean) || []),
        ]
        const seen = new Set()
        return allUsers.filter((user) => {
            if (!user?.id || seen.has(user.id)) return false
            seen.add(user.id)
            return true
        })
    }, [boardDirectUsers, task?.assignedTo])
    const boardAllowedUserIds = useMemo(() => boardDirectUsers.map((user) => user.id), [boardDirectUsers])

    const { loading: commentsLoading, error: commentsError, data: commentsData, refetch: refetchTaskCommentsQuery } = useQuery(AllTaskCommentsQuery, {
        variables: { taskId: taskId },
        fetchPolicy: 'cache-and-network',
        skip: !taskId,
    })
    const taskCommentsData = commentsData?.allTaskComments
    const [showEditIcon, setShowEditIcon] = useState(false)
    const [isEditingTitle, setIsEditingTitle] = useState(false)
    const [editedTitle, setEditedTitle] = useState(task?.title || "")
    const [isEditingDescription, setIsEditingDescription] = useState(false)
    const [editedDescription, setEditedDescription] = useState("")
    const [showEditDescriptionIcon, setShowEditDescriptionIcon] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [activeReply, setActiveReply] = useState(null)

    useEffect(() => {
        if (!isEditingTitle) {
            setEditedTitle(task?.title || "")
        }
    }, [task?.title, isEditingTitle])

    useEffect(() => {
        if (!isEditingDescription) {
            setEditedDescription(task?.description || "")
        }
    }, [task?.description, isEditingDescription])

    const refetchTaskData = async () => {
        if (refetchTaskCommentsQuery) {
            await refetchTaskCommentsQuery()
        }
        if (refetchTaskQuery) {
            await refetchTaskQuery()
        }
    }

    const toggleChecklistItem = (checklistId, value) => {
        try {
            const input = {
                checklistId: checklistId, isCompleted: value,
            }

            props.apolloClient.mutate({
                mutation: UpdateTaskChecklistMutation,
                variables: { input },
            }).then((result) => {
                if (result && result.data && result.data.updateTaskChecklist && result.data.updateTaskChecklist.ok) {
                    setPanelError("")
                    refetchTaskData()
                }
                if (!result || !result.data || !result.data.updateTaskChecklist || !result.data.updateTaskChecklist.ok) { throw new Error("Error updating checklist item") }
            }).catch((error) => {
                setPanelError("Error updating checklist item: " + error)
            })
        } catch (error) {
            setPanelError("Error updating checklist item :" + error)
        }
    }

    const createChecklist = () => {
        try {
            const input = {
                taskId: task.id,
                title: newChecklistTitle,
            }

            props.apolloClient.mutate({
                mutation: CreateTaskChecklistMutation,
                variables: { input },
            }).then((result) => {
                if (result && result.data && result.data.createTaskChecklist && result.data.createTaskChecklist.ok) {
                    setPanelError("")
                    refetchTaskData()
                }
                if (!result || !result.data || !result.data.createTaskChecklist || !result.data.createTaskChecklist.ok) { throw new Error("Error creating checklist item") }
                setNewChecklistTitle("")
            }).catch((error) => {
                setPanelError("Error creating checklist item :" + error)
            })
        } catch (error) {
            setPanelError("Error creating checklist item :" + error)
        }
    }

    const history_item_ids = [task?.id || taskId]
    if (taskCommentsData?.edges)
        history_item_ids.push(...taskCommentsData.edges.map(edge => edge.node.id))
    const allComments = useMemo(
        () => taskCommentsData?.edges?.map((edge) => edge.node) || [],
        [taskCommentsData]
    )
    const commentThreads = useMemo(() => {
        const childCommentsByParent = new Map()
        const topLevelComments = []

        allComments.forEach((comment) => {
            const parentId = comment?.parentComment?.id
            if (!parentId) {
                topLevelComments.push(comment)
                return
            }

            if (!childCommentsByParent.has(parentId)) {
                childCommentsByParent.set(parentId, [])
            }
            childCommentsByParent.get(parentId).push(comment)
        })

        childCommentsByParent.forEach((children, parentId) => {
            childCommentsByParent.set(
                parentId,
                [...children].sort((left, right) => new Date(left.dateAdded) - new Date(right.dateAdded))
            )
        })

        return topLevelComments.map((comment) => ({
            parent: comment,
            children: childCommentsByParent.get(comment.id) || [],
        }))
    }, [allComments])

    const handleReply = (comment) => {
        const parentComment = comment?.parentComment || comment
        setActiveReply({
            parentCommentId: parentComment?.id,
            mentionUser: comment?.createdBy || null,
        })
    }

    if (taskLoading && !task) {
        return (
            <div className="TaskDetailsSidePanelContainer">
                <div className="TaskDetailsSidePanel show">
                    <Skeleton height={50} width={200} />
                    <Skeleton count={3} height={30} />
                    <Skeleton height={150} />
                    <Skeleton count={2} height={20} />
                </div>
            </div>
        )
    }

    const handleSaveTaskField = async (field, value) => {
        if (!field) return
        setIsSaving(true)

        const input = {
            taskId,
            [field]: value,
        }

        try {
            const result = await props.apolloClient.mutate({
                mutation: UpdateTaskMutation,
                variables: { input },
            })

            if (result?.data?.updateTask?.ok) {
                setPanelError("")
                await refetchTaskData()
            } else if (result?.data?.updateTask?.errors[0]?.messages) {
                setPanelError(result.data.updateTask.errors[0].messages.toString())
                return
            } else {
                setPanelError(`Error updating task ${field}.`)
                return
            }

            if (field === "title") setIsEditingTitle(false)
            if (field === "description") setIsEditingDescription(false)
        } catch (error) {
            setPanelError(error?.message || `Error updating task ${field}.`)
        }
        finally {
            setIsSaving(false)
        }
    }

    const clearTaskId = (e) => {
        e.preventDefault()
        const next = new URLSearchParams(searchParams)
        next.delete('task_id')
        setSearchParams(next, { replace: true }) // keep other params, replace history
    }

    return (
        <div>
            <div className="TaskDetailsSidePanelContainer" >
                <div className={`TaskDetailsSidePanel show`}>
                    {/* on click of the close button, go back to the previous page */}
                    {/* <Link to={`/boards${boardId ? "/" + boardId : ""}/tasks/`} className="btn btn-outline-dark"> */}
                    <div className="TaskDetailsSidePanelHeader d-flex justify-content-between align-items-center">
                        <Link onClick={clearTaskId} className="close-button">
                            <i className="fa fa-times my-3" aria-hidden="true"></i>
                        </Link>
                        {!task?.isAutomated && <TaskArchievePopover taskId={task?.id} onArchived={clearTaskId} />}
                    </div>
                    {(error || commentsError || panelError) && (
                        <div className="alert alert-danger">
                            {error?.message || commentsError?.message || panelError}
                        </div>
                    )}
                    <div className="editable-title-container d-inline-flex align-items-center" onMouseEnter={() => !task?.isAutomated && setShowEditIcon(true)} onMouseLeave={() => setShowEditIcon(false)}>
                        {!isEditingTitle || task?.isAutomated ? (
                            <>
                                <h3 className="task-title">{task?.title || "Untitled"}</h3>
                                {!task?.isAutomated && (
                                    <span
                                        className={`edit-icon-box ml-2 ${showEditIcon ? "edit-icon-box--visible" : ""}`}
                                        onClick={() => { setEditedTitle(task?.title || ""); setIsEditingTitle(true) }}
                                    >
                                        <i className="fa fa-pencil-square-o"></i>
                                    </span>
                                )}
                            </>
                        ) : (
                            <div className="task-inline-editor">
                                <input
                                    type="text"
                                    className="form-control title-edit-input"
                                    value={editedTitle}
                                    autoFocus
                                    onChange={(e) => setEditedTitle(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") handleSaveTaskField("title", editedTitle)
                                        if (e.key === "Escape") {
                                            setEditedTitle(task?.title || "")
                                            setIsEditingTitle(false)
                                        }
                                    }}
                                />
                                <div className="task-inline-editor__actions">
                                    <Button color="link" className="task-inline-editor__cancel" disabled={isSaving} onClick={() => { setEditedTitle(task?.title || ""); setIsEditingTitle(false) }}>
                                        Cancel
                                    </Button>
                                    <Button color="primary" size="sm" disabled={isSaving || editedTitle === (task?.title || "")} onClick={() => handleSaveTaskField("title", editedTitle)}>
                                        {isSaving ? <i className="fa fa-spinner fa-spin"></i> : "Save"}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    <br />

                    <div className="editable-title-container d-inline-flex align-items-start" onMouseEnter={() => setShowEditDescriptionIcon(true)} onMouseLeave={() => setShowEditDescriptionIcon(false)}>
                        {!isEditingDescription ? (
                            <>
                                <p className="task-description mb-0">{task?.description || "No description"}</p>
                                <span
                                    className={`edit-icon-box ml-2 ${showEditDescriptionIcon ? "edit-icon-box--visible" : ""}`}
                                    onClick={() => { setEditedDescription(task?.description || ""); setIsEditingDescription(true) }}
                                >
                                    <i className="fa fa-pencil-square-o"></i>
                                </span>
                            </>
                        ) : (
                            <div className="task-inline-editor task-inline-editor--description">
                                <textarea
                                    className="form-control title-edit-input"
                                    style={{ width: "500px" }}
                                    value={editedDescription}
                                    autoFocus
                                    onChange={(e) => setEditedDescription(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && e.ctrlKey) handleSaveTaskField("description", editedDescription)
                                        if (e.key === "Escape") {
                                            setEditedDescription(task?.description || "")
                                            setIsEditingDescription(false)
                                        }
                                    }}
                                />
                                <div className="task-inline-editor__actions">
                                    <Button color="link" className="task-inline-editor__cancel" disabled={isSaving} onClick={() => { setEditedDescription(task?.description || ""); setIsEditingDescription(false) }}>
                                        Cancel
                                    </Button>
                                    <Button color="primary" size="sm" disabled={isSaving || editedDescription === (task?.description || "")} onClick={() => handleSaveTaskField("description", editedDescription)}>
                                        {isSaving ? <i className="fa fa-spinner fa-spin"></i> : "Save"}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                    <Nav tabs className="mt-2">
                        <NavItem className={activeTab === "#Updates" ? "active" : "inactive"}><NavLink onClick={() => setActiveTab("#Updates")} active={activeTab === "#Updates"} style={{ cursor: "pointer" }}>Updates</NavLink></NavItem>
                        <NavItem className={activeTab === "#Fields" ? "active" : "inactive"}><NavLink onClick={() => setActiveTab("#Fields")} active={activeTab === "#Fields"} style={{ cursor: "pointer" }}>Fields</NavLink></NavItem>
                        <NavItem className={activeTab === "#History" ? "active" : "inactive"}><NavLink onClick={() => setActiveTab("#History")} active={activeTab === "#History"} style={{ cursor: "pointer" }}>History</NavLink></NavItem>
                    </Nav>
                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="#Updates">
                            <NewCommentForm commentId={task?.id} refetch={refetchTaskData} boardUsers={boardDirectUsers} {...props} />
                            <Divider className="my-4" />
                            {commentsLoading && <Skeleton height={24} count={3} />}
                            {commentThreads.map((thread) => (
                                <div key={thread.parent.id} className="comment-thread">
                                    <CommentCard
                                        comment={thread.parent}
                                        currentUsername={currentUser?.username}
                                        refetch={refetchTaskData}
                                        onReply={handleReply}
                                        boardId={task?.board?.id}
                                        boardUsers={boardDirectUsers}
                                        {...props}
                                    />
                                    {thread.children.length > 0 && (
                                        <div className="comment-thread__children">
                                            {thread.children.map((childComment) => (
                                                <CommentCard
                                                    key={childComment.id}
                                                    comment={childComment}
                                                    currentUsername={currentUser?.username}
                                                    refetch={refetchTaskData}
                                                    onReply={handleReply}
                                                    isChild
                                                    boardId={task?.board?.id}
                                                    boardUsers={boardDirectUsers}
                                                    {...props}
                                                />
                                            ))}
                                        </div>
                                    )}
                                    {activeReply?.parentCommentId === thread.parent.id && (
                                        <div className="comment-thread__reply">
                                            <NewCommentForm
                                                key={`${activeReply.parentCommentId}-${activeReply.mentionUser?.id || "none"}`}
                                                commentId={task?.id}
                                                parentCommentId={activeReply.parentCommentId}
                                                initialMentionUser={activeReply.mentionUser}
                                                submitLabel="Reply"
                                                boardUsers={boardDirectUsers}
                                                refetch={async () => {
                                                    await refetchTaskData()
                                                    setActiveReply(null)
                                                }}
                                                onCancelReply={() => setActiveReply(null)}
                                                {...props}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </TabPane>
                        <TabPane tabId="#Fields">
                            <TaskFieldsTab task={task} apolloClient={props.apolloClient} refetch={refetchTaskData} boardUsers={boardDirectUsers} boardAllowedUserIds={boardAllowedUserIds} assignableUserOptions={assignableUserOptions} />
                        </TabPane>
                        <TabPane tabId="#History">
                            <HistoryList objectIds={history_item_ids} />
                        </TabPane>
                        <TabPane tabId="#Checklist">
                            <ul className="list-unstyled">
                                {task && task?.taskchecklistSet && task?.taskchecklistSet.edges && task?.taskchecklistSet.edges.length > 0 &&
                                    task?.taskchecklistSet.edges.map((checklist, index) => (
                                        checklist = checklist.node,
                                        <li key={index} className="mb-2">
                                            <Row className="align-items-center">
                                                <Col className="text-left">
                                                    <FormGroup check>
                                                        <Input
                                                            id={checklist.id}
                                                            type="checkbox"
                                                            checked={checklist.completedOn}
                                                            onChange={() => toggleChecklistItem(checklist.id, !checklist.completedOn)}
                                                        />
                                                        {' '}
                                                        <Label check for={checklist.id}>
                                                            <span style={{ textDecoration: checklist.completedOn ? "line-through" : "none" }}>{checklist.title}</span>
                                                        </Label>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </li>
                                    ))}
                                {/* New checklist input text with submit button on the right */}
                                <li className="mt-4">
                                    <Row className="align-items-center">
                                        <Col className="text-left">
                                            <FormGroup>
                                                <Input
                                                    value={newChecklistTitle}
                                                    onChange={(e) => setNewChecklistTitle(e.target.value)}
                                                    type="text" name="checklist"
                                                    id="checklist"
                                                    placeholder="Add a new checklist item"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col className="text-right">
                                            <Button onClick={createChecklist} color="primary">Add</Button>
                                        </Col>
                                    </Row>
                                </li>
                            </ul>
                        </TabPane>
                    </TabContent>

                </div>
            </div>
        </div>
    )
}


function TaskFieldsTab({ task, apolloClient, refetch, boardUsers = [], boardAllowedUserIds = [], assignableUserOptions = [] }) {
    const [editingField, setEditingField] = useState(null) // field key being edited
    const [editValue, setEditValue] = useState(null)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState(null)
    const { data: taskStatusData } = useQuery(TaskStatusOptionsQuery)

    const customColumns = task?.board?.columnSet?.edges
        ?.filter(({ node }) => !node.isArchived)
        .map(({ node }) => node) || []

    const valueMap = {}
    task?.taskcolumnvalueSet?.edges?.forEach(({ node }) => {
        valueMap[node.column.id] = node
    })
    const statusOptions = (taskStatusData?.optionsList?.enumValues || []).map((status) => ({
        value: status.name,
        label: status.description || status.name,
    }))
    const filteredStatusOptions = task?.isAutomated
        ? statusOptions.filter(({ value }) => !["DONE", "CANCELED", "CANCELLED"].includes(value))
        : statusOptions
    const canEditCustomFields = !task?.isAutomated

    const startEdit = (fieldKey, initialValue) => {
        setEditingField(fieldKey)
        setEditValue(initialValue)
        setError(null)
    }

    const cancelEdit = () => {
        setEditingField(null)
        setEditValue(null)
    }

    const saveStandardField = async (fieldKey, value) => {
        setSaving(true)
        setError(null)
        try {
            const result = await apolloClient.mutate({
                mutation: UpdateTaskMutation,
                variables: { input: { taskId: task.id, [fieldKey]: value } },
                refetchQueries: ['AllTasks'],
            })
            if (!result?.data?.updateTask?.ok) {
                const msg = result?.data?.updateTask?.errors?.[0].messages?.[0] || 'Failed to save'
                setError(msg)
            }
            if (refetch) await refetch()
            setEditingField(null)
        } catch (e) {
            setError(e.message || 'Failed to save')
        } finally {
            setSaving(false)
        }
    }

    const saveCustomColumn = async (col) => {
        if (!canEditCustomFields) {
            setError("Only status, due date and assigned to are editable for automated tasks.")
            return
        }
        setSaving(true)
        setError(null)
        try {
            const result = await apolloClient.mutate({
                mutation: UpdateTaskMutation,
                variables: { input: { taskId: task.id, columnValues: [{ columnId: col.id, value: editValue || '' }] } },
                refetchQueries: ['AllTasks'],
            })
            if (!result?.data?.updateTask?.ok) {
                const msg = result?.data?.updateTask?.errors?.[0].messages?.[0] || 'Failed to save'
                throw new Error(msg)
            }
            if (refetch) await refetch()
            setEditingField(null)
        } catch (e) {
            setError(e.message || 'Failed to save')
        } finally {
            setSaving(false)
        }
    }

    const renderSaveCancel = (onSave) => (
        <div className="mt-1 d-flex">
            <Button size="sm" color="primary" onClick={onSave} disabled={saving}>
                {saving ? <i className="fa fa-spinner fa-spin" /> : 'Save'}
            </Button>
            <Button size="sm" color="secondary" className="ml-1" onClick={cancelEdit} disabled={saving}>Cancel</Button>
        </div>
    )

    const renderCustomColInput = (col) => {
        if (col.columnType === 'MIRROR') {
            return null
        }
        if (col.columnType === 'ENUM') {
            const options = (col.allowedOptions || []).map(opt => ({ value: opt, label: opt }))
            return (
                <Select
                    options={options}
                    value={editValue ? { value: editValue, label: editValue } : null}
                    onChange={(s) => setEditValue(s ? s.value : '')}
                    isClearable
                    autoFocus
                />
            )
        }
        if (col.columnType === 'FK_OBJECT') {
            return <ForeignKeyInput model={col.contentType?.model} onChange={setEditValue} defaultValue={valueMap[col.id]?.value} />
        }
        if (col.columnType === 'DATE') {
            return <Input type="date" value={editValue || ''} onChange={(e) => setEditValue(e.target.value)} autoFocus />
        }
        return (
            <Input
                type="text"
                value={editValue || ''}
                autoFocus
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveCustomColumn(col) }}
            />
        )
    }

    const FieldRow = ({ label, displayValue, fieldKey, onEdit, strikethrough, children }) => {
        const isEditing = editingField === fieldKey
        return (
            <div className={`task-field-row ${!onEdit ? 'task-field-row--readonly' : ''}`}>
                <div className="task-field-label">{label}</div>
                <div className="task-field-value">
                    {isEditing ? (
                        <div>{children}</div>
                    ) : (
                        <div className="d-flex align-items-center task-field-display">
                            <span className={displayValue ? '' : 'text-muted'} style={strikethrough ? { textDecoration: "line-through" } : {}}>{displayValue || '—'}</span>
                            {onEdit && (
                                <span className="edit-icon-box ml-2" onClick={onEdit}>
                                    <i className="fa fa-pencil-square-o" />
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className="task-fields-tab mt-3">
            {error && <div className="alert alert-danger py-1 mb-2">{error}</div>}

            {/* Standard task fields */}
            <FieldRow
                label="Status"
                fieldKey="status"
                displayValue={task?.status}
                onEdit={() => startEdit('status', task?.status || '')}
            >
                <Select
                    options={filteredStatusOptions}
                    value={filteredStatusOptions.find(o => o.value === editValue) || null}
                    onChange={(s) => setEditValue(s ? s.value : '')}
                    autoFocus
                />
                {renderSaveCancel(() => saveStandardField('status', editValue))}
            </FieldRow>

            <FieldRow
                label="Due Date"
                fieldKey="dueDate"
                displayValue={task?.dueDate ? moment(task.dueDate).format('MMM D, YYYY') : null}
                strikethrough={task?.status === "DONE"}
                onEdit={() => startEdit('dueDate', task?.dueDate ? task.dueDate.split('T')[0] : '')}
            >
                <Input type="date" value={editValue || ''} onChange={(e) => setEditValue(e.target.value)} autoFocus />
                {renderSaveCancel(() => saveStandardField('dueDate', editValue))}
            </FieldRow>

            <FieldRow
                label="Assigned To"
                fieldKey="assignedTo"
                displayValue={task?.assignedTo?.edges?.length > 0
                    ? task.assignedTo.edges.map(({ node: u }) => u.firstName ? `${u.firstName} ${u.lastName}` : u.username).join(', ')
                    : null}
                onEdit={() => startEdit('assignedTo', task?.assignedTo?.edges?.map(({ node }) => node.id) || [])}
            >
                <RepsTypeAhead
                    defaultValue={task?.assignedTo?.edges?.map(({ node }) => node.id) || []}
                    onChange={(ids) => setEditValue(ids)}
                    group={[]}
                    allowedRepIds={boardAllowedUserIds}
                    extraRepOptions={assignableUserOptions}
                />
                {renderSaveCancel(() => saveStandardField('assignedToIds', editValue || []))}
            </FieldRow>

            <FieldRow
                label="Date Added"
                fieldKey="dateAdded"
                displayValue={task?.dateAdded ? moment(task.dateAdded).format('MMM D, YYYY h:mm A') : null}
            />

            <FieldRow
                label="Created By"
                fieldKey="createdBy"
                displayValue={task?.createdBy
                    ? (task.createdBy.firstName ? `${task.createdBy.firstName} ${task.createdBy.lastName}` : task.createdBy.username)
                    : null}
            />

            {/* Custom board columns */}
            {customColumns.map(col => {
                const currentValNode = valueMap[col.id]
                const displayValue = currentValNode?.valueString
                const isMirrorColumn = col.columnType === 'MIRROR'
                const isEditing = canEditCustomFields && !isMirrorColumn && editingField === `col_${col.id}`
                return (
                    <div key={col.id} className={`task-field-row ${(!canEditCustomFields || isMirrorColumn) ? 'task-field-row--readonly' : ''}`}>
                        <div className="task-field-label">{col.title}</div>
                        <div className="task-field-value">
                            {isEditing ? (
                                <div>
                                    {renderCustomColInput(col)}
                                    {renderSaveCancel(() => saveCustomColumn(col))}
                                </div>
                            ) : (
                                <div className="d-flex align-items-center task-field-display">
                                    {renderCustomColumn(currentValNode)}
                                    {canEditCustomFields && !isMirrorColumn && (
                                        <span className="edit-icon-box ml-2" onClick={() => startEdit(`col_${col.id}`, displayValue || '')}>
                                            <i className="fa fa-pencil-square-o" />
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

const getCommentAuthorName = (user) => {
    if (!user) return "Unknown user"
    if (user.firstName || user.lastName) {
        return [user.firstName, user.lastName].filter(Boolean).join(" ")
    }
    return user.username || "Unknown user"
}

const buildMentionMarkup = (user) => (
    user?.id ? `@[${getCommentAuthorName(user)}](${user.id}) ` : ""
)

const renderMentionSuggestion = (suggestion, highlightedDisplay) => (
    <div className="user-suggestion">
        <div className="user-suggestion__avatar">
            <RepAvatar rep={suggestion} keepClose />
        </div>
        <div className="user-suggestion__content">
            <div className="user-suggestion__name">{highlightedDisplay}</div>
            <div className="user-suggestion__meta">
                @{suggestion?.username || suggestion?.display || "user"}
            </div>
        </div>
    </div>
)

const convertUrlsToMarkdownLinks = (value) => {
    if (!value) return ""
    return value.replace(/(^|[\s(])((https?:\/\/[^\s<]+))/g, (match, prefix, url) => `${prefix}[${url}](${url})`)
}

const searchRepsWithClient = async (apolloClient, search, callBack, { boardUsers = [] } = {}) => {
    try {
        const mergedReps = [...boardUsers]
        const normalizedSearch = (search || "").trim().toLowerCase()
        const uniqueReps = []
        const seen = new Set()
        mergedReps.forEach((rep) => {
            if (!rep?.id || seen.has(rep.id)) return
            seen.add(rep.id)
            const display = rep.firstName ? `${rep.firstName} ${rep.lastName}` : rep.username
            const matchesSearch = !normalizedSearch || [display, rep.username, rep.firstName, rep.lastName]
                .filter(Boolean)
                .some((value) => value.toLowerCase().includes(normalizedSearch))
            if (matchesSearch) {
                uniqueReps.push({
                    id: rep.id,
                    display,
                    username: rep.username,
                    firstName: rep.firstName,
                    lastName: rep.lastName,
                })
            }
        })

        callBack(uniqueReps)
    } catch (error) {
        console.log("Error loading reps", error)
    }
}

const fileToBase64Payload = async (file) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    const data = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result)
        reader.onerror = (error) => reject(error)
    })
    return { name: file.name, type: file.type, data }
}

const renderPendingAttachmentPreviews = (files, onRemove) => {
    if (!files || files.length === 0) {
        return null
    }

    return (
        <div className="d-flex flex-wrap gap-3 mt-2">
            {files.map((file, index) => {
                const url = URL.createObjectURL(file)

                return (
                    <div key={`${file.name}-${file.lastModified}-${index}`} className="p-2 border rounded d-flex flex-column align-items-center" style={{ width: "120px", position: "relative" }}>
                        <button
                            type="button"
                            className="btn btn-sm btn-light attachment-remove-button"
                            onClick={() => onRemove(index)}
                            aria-label={`Remove ${file.name}`}
                            title="Remove attachment"
                        >
                            ×
                        </button>
                        <div className="text-truncate w-100 small mb-1" title={file.name}>
                            {file.name}
                        </div>

                        {file.type.includes("image") && (
                            <img src={url} alt={file.name} style={{ width: "100%", height: "80px", objectFit: "cover" }} />
                        )}

                        {file.type.includes("video") && (
                            <video src={url} style={{ width: "100%", height: "80px", objectFit: "cover" }} controls />
                        )}

                        {file.type.includes("audio") && (
                            <audio src={url} controls style={{ width: "100%" }} />
                        )}

                        {file.type.includes("pdf") && (
                            <iframe src={url} style={{ width: "100%", height: "80px", border: 0 }} title={file.name} />
                        )}
                    </div>
                )
            })}
        </div>
    )
}

export function CommentCard({ comment, currentUsername, refetch, onReply, isChild = false, boardId = null, boardUsers = [], ...props }) {
    const [isEditing, setIsEditing] = useState(false)
    const [editedComment, setEditedComment] = useState(comment.comment)
    const [editFiles, setEditFiles] = useState([])
    const [removedAttachmentIds, setRemovedAttachmentIds] = useState([])
    const [saving, setSaving] = useState(false)
    const [previewUrl, setPreviewUrl] = useState(null)
    const [previewType, setPreviewType] = useState(null)
    const canEditComment = currentUsername && currentUsername === comment?.createdBy?.username
    const editFileInputRef = useRef(null)

    if (!comment) {
        return null
    }

    const handleCommentEdit = (fullComment) => {
        if (!fullComment) return
        setEditedComment(fullComment.comment)   // keep old value
        setEditFiles([])
        setRemovedAttachmentIds([])
        setIsEditing(true)
    }

    const removeEditFileAtIndex = (indexToRemove) => {
        setEditFiles((prev) => prev.filter((_, index) => index !== indexToRemove))
    }

    const removeExistingAttachment = (attachmentId) => {
        setRemovedAttachmentIds((prev) => [...prev, attachmentId])
    }

    const handleSave = async () => {
        try {
            setSaving(true)

            const filesInBase64 = await Promise.all(editFiles.map(fileToBase64Payload))
            const existingAttachmentIds = comment?.taskcommentattachmentSet?.edges?.map(({ node }) => node?.id).filter(Boolean) || []
            const removeAttachmentIds = removedAttachmentIds.filter((id) => existingAttachmentIds.includes(id))

            const input = {
                commentId: comment.id,
                comment: editedComment,
                files: filesInBase64,
                removeAttachmentIds,
            }

            await props.apolloClient.mutate({
                mutation: UpdateTaskCommentMutation,
                variables: { input },
            })
            if (refetch) {
                await refetch()
            }

            setIsEditing(false)
            setEditFiles([])
            setRemovedAttachmentIds([])
        } catch (e) {
            console.log("Save error", e)
        } finally {
            setSaving(false)
        }
    }

    const visibleExistingAttachments = (comment?.taskcommentattachmentSet?.edges || [])
        .map(({ node }) => node)
        .filter((attachment) => attachment?.id && !removedAttachmentIds.includes(attachment.id))

    return (
        <div className={`comment-shell ${isChild ? "comment-shell--child" : "comment-shell--parent"}`}>
            {isChild && (
                <div className="comment-shell__avatar">
                    <RepAvatar rep={comment.createdBy} keepClose />
                </div>
            )}
            <div className="comment-shell__main">
                <Card className={`my-0 CommentCard ${isChild ? "CommentCard--child" : ""}`}>
                    <CardBody>
                        <div className="comment-card-header-row">
                            <div className="comment-card-header-main">
                                {isChild ? (
                                    <div className="comment-card-author">{getCommentAuthorName(comment.createdBy)}</div>
                                ) : (
                                    <span className="comment-card-rep">
                                        <RepAvatar rep={comment.createdBy} keepOpen />
                                    </span>
                                )}
                                <div className="comment-card-date"><DatetimeRenderer datetime={comment.dateAdded} /></div>
                            </div>
                            <div className="comment-card-header-actions">
                                <EditCommentPopover comment={comment} canEdit={canEditComment} onEdit={handleCommentEdit} refetch={refetch} />
                            </div>
                        </div>
                        {isEditing ? (
                            <div className="NewCommentForm NewCommentForm--reply comment-edit-form">
                                <FormGroup className="NewCommentTextAreaFormGroup">
                                    <div className="MentionsInputContainer position-relative">
                                        <MentionsInput value={editedComment} onChange={(e) => setEditedComment(e.target.value)} className="text-area">
                                            <Mention
                                                trigger="@"
                                                data={(search, callBack) => searchRepsWithClient(props.apolloClient, search, callBack, { boardUsers })}
                                                displayTransform={(id, display) => `@${display}`}
                                                appendSpaceOnAdd={true}
                                                renderSuggestion={(suggestion, search, highlightedDisplay) => renderMentionSuggestion(suggestion, highlightedDisplay)}
                                            />
                                        </MentionsInput>
                                        <button
                                            type="button"
                                            className="pin-icon"
                                            title="Attach a file"
                                            aria-label="Attach a file"
                                            onClick={() => editFileInputRef.current?.click()}
                                        >
                                            <i className="fa fa-paperclip" aria-hidden="true"></i>
                                        </button>
                                        <input
                                            ref={editFileInputRef}
                                            type="file"
                                            style={{ display: "none" }}
                                            multiple
                                            onChange={(e) => {
                                                const selectedFiles = Array.from(e.target.files || [])
                                                setEditFiles((prev) => [...prev, ...selectedFiles])
                                                e.target.value = ""
                                            }}
                                        />
                                    </div>
                                </FormGroup>
                                {(visibleExistingAttachments.length > 0 || editFiles.length > 0) && (
                                    <div className="comment-edit-attachments">
                                        {visibleExistingAttachments.length > 0 && (
                                            <div className="comment-edit-attachments__section">
                                                <div className="comment-edit-attachments__label">Current attachments</div>
                                                <div className="attachment-row">
                                                    {visibleExistingAttachments.map((attachment) => (
                                                        <div key={attachment.id} className="attachment-box attachment-box--editable">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-light attachment-remove-button"
                                                                onClick={() => removeExistingAttachment(attachment.id)}
                                                                aria-label={`Remove ${attachment.name}`}
                                                                title="Remove attachment"
                                                            >
                                                                ×
                                                            </button>
                                                            <div className="attachment-name" title={attachment.name}>
                                                                {attachment.name.length > 20 ? `${attachment.name.substring(0, 20)}...` : attachment.name}
                                                            </div>
                                                            {["png", "jpeg", "jpg", "tiff", "webp", "gif"].includes(attachment.extension) && (
                                                                <img className="attachment-thumbnail" src={attachment.url} alt={attachment.name} />
                                                            )}
                                                            {["mp4"].includes(attachment.extension) && (
                                                                <video className="attachment-thumbnail" src={attachment.url} />
                                                            )}
                                                            {["mp3"].includes(attachment.extension) && (
                                                                <audio className="attachment-audio" src={attachment.url} controls />
                                                            )}
                                                            {["pdf"].includes(attachment.extension) && (
                                                                <div className="attachment-pdf"><i className="fa fa-file-pdf-o fa-3x text-danger"></i></div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {editFiles.length > 0 && (
                                            <div className="comment-edit-attachments__section">
                                                <div className="comment-edit-attachments__label">New attachments</div>
                                                {renderPendingAttachmentPreviews(editFiles, removeEditFileAtIndex)}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="update-submit-button-container">
                                    <Button
                                        type="button"
                                        color="link"
                                        className="comment-reply-cancel"
                                        onClick={() => {
                                            setIsEditing(false)
                                            setEditedComment(comment.comment)
                                            setEditFiles([])
                                            setRemovedAttachmentIds([])
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button type="button" onClick={handleSave} disabled={saving}>
                                        {saving ? "Saving..." : "Save"}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <CardText className='font comment-card-text'>
                                <ReactMarkdown
                                    components={{
                                        a: ({ href, children }) => (
                                            href && /^(https?:|mailto:|tel:)/.test(href)
                                                ? <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
                                                : <span className="comment-mention">{children}</span>
                                        ),
                                    }}
                                >
                                    {convertUrlsToMarkdownLinks(comment.comment)}
                                </ReactMarkdown>
                            </CardText>
                        )}

                        {!isEditing && comment.taskcommentattachmentSet && comment.taskcommentattachmentSet.edges && comment.taskcommentattachmentSet.edges.length > 0 && (
                            <div className="comment-card-attachments">
                                <div className="attachment-row">
                                    {comment.taskcommentattachmentSet.edges.map((item, index) => {
                                        let attachment = item.node
                                        const openPreview = (type) => {
                                            setPreviewUrl(attachment.url)
                                            setPreviewType(type)
                                        }
                                        return (
                                            <div key={index} className="attachment-box" onClick={() => openPreview(["png", "jpeg", "jpg", "tiff", "webp", "gif"].includes(attachment.extension) ? "image" : ["mp4"].includes(attachment.extension) ? "video" : ["mp3"].includes(attachment.extension) ? "audio" : ["pdf"].includes(attachment.extension) ? "pdf" : null)}                                    >
                                                <div className="attachment-name">{attachment.name.length > 20 ? attachment.name.substring(0, 20) + "..." : attachment.name}</div>
                                                {["png", "jpeg", "jpg", "tiff", "webp", "gif"].includes(attachment.extension) && (
                                                    <img className="attachment-thumbnail" src={attachment.url} alt={attachment.name} />
                                                )}
                                                {["mp4"].includes(attachment.extension) && (
                                                    <video className="attachment-thumbnail" src={attachment.url} />
                                                )}

                                                {["mp3"].includes(attachment.extension) && (
                                                    <audio className="attachment-audio" src={attachment.url} controls />
                                                )}

                                                {["pdf"].includes(attachment.extension) && (
                                                    <div className="attachment-pdf"><i className="fa fa-file-pdf-o fa-3x text-danger"></i></div>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </CardBody>
                </Card>
                <div className="comment-card-footer-row">
                    <ReactionsPopup commentId={comment.id} />
                    <button type="button" className="comment-reply-button" onClick={() => onReply && onReply(comment)}>
                        <i className="fa fa-reply" aria-hidden="true"></i>
                        <span>Reply</span>
                    </button>
                </div>
            </div>

            <Modal isOpen={!!previewUrl} toggle={() => setPreviewUrl(null)} size="lg">
                <ModalHeader toggle={() => setPreviewUrl(null)}>Preview</ModalHeader>
                <ModalBody>
                    {{
                        image: <DocumentViewer documentURL={previewUrl} handleClose={() => setPreviewUrl(null)} documentName={"Uploaded Document"} />,
                        video: <video src={previewUrl} style={{ width: "100%" }} controls />,
                        audio: <audio src={previewUrl} style={{ width: "100%" }} controls />,
                        pdf: <iframe src={previewUrl} style={{ width: "100%", height: "80vh" }} />
                    }[previewType] || null}
                </ModalBody>
            </Modal>
        </div>
    )
}

export function NewCommentForm({ commentId, parentCommentId = null, initialMentionUser = null, submitLabel = "Add update", refetch, onCancelReply = null, boardUsers = [], ...props }) {
    const { currentUser } = useContext(CurrentUserContext)
    const [commentBody, setCommentBody] = useState("")
    const [error, setError] = useState("")
    const [loading, setLoading] = useState(false)
    const [files, setFiles] = useState([])
    const fileInputRef = useRef(null)

    let inp = useRef()
    useEffect(() => {
        if (!inp && !inp.current) return
        // inp.current.focus()
        return () => inp = null
    })

    useEffect(() => {
        setCommentBody(buildMentionMarkup(initialMentionUser))
        setError("")
        setFiles([])
    }, [initialMentionUser?.id, parentCommentId])

    // Submit when user presses Shift + Enter
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Enter" && e.shiftKey) {
                createTaskComment(e)
            }
        }
        document.addEventListener("keydown", handleKeyDown)
        return () => document.removeEventListener("keydown", handleKeyDown)
    }, [commentBody])

    const handlePaste = (e) => {
        const clipboard = e.clipboardData
        if (!clipboard) return

        const pastedFiles = Array.from(clipboard.files || []).length > 0
            ? Array.from(clipboard.files || [])
            : Array.from(clipboard.items || [])
                .filter((item) => item.kind === "file")
                .map((item) => item.getAsFile())
                .filter(Boolean)

        const pastedImages = pastedFiles.filter((file) => file?.type?.startsWith("image/"))
        if (!pastedImages.length) return

        const normalizedFiles = pastedImages.map((file, index) => {
            if (file.name) return file
            const extension = file?.type?.split("/")?.[1] || "png"
            return new File([file], `pasted-file-${Date.now()}-${index}.${extension}`, { type: file.type })
        })

        setFiles((prev) => {
            const merged = [...prev, ...normalizedFiles]
            return merged.filter((file, index, arr) =>
                arr.findIndex((f) =>
                    f.size === file.size &&
                    f.type === file.type &&
                    f.lastModified === file.lastModified
                ) === index
            )
        })
    }

    const removeFileAtIndex = (indexToRemove) => {
        setFiles((prev) => prev.filter((_, index) => index !== indexToRemove))
    }

    const createTaskComment = async (e) => {
        e.preventDefault()
        try {
            if (!props.apolloClient || !commentId) {
                throw new Error("GQL Client not found")
            }
            if (!commentBody || commentBody.length < 5) {
                setError("Comment is required or too short")
                return
            }
            setLoading(true)
            const filesInBase64 = await Promise.all(files.map(fileToBase64Payload))

            const input = {
                taskId: commentId,
                parentCommentId,
                comment: commentBody,
                files: filesInBase64
            }

            props.apolloClient.mutate({
                mutation: CreateTaskCommentMutation,
                variables: { input },
            }).then((result) => {
                setLoading(false)
                if (!result) {
                    throw new Error("Error creating task update")
                }
                if (result.data && result.data.createTaskComment && result.data.createTaskComment.ok) {
                    setError("")
                    setCommentBody("")
                    setFiles([])
                    if (refetch) {
                        refetch()
                    }

                } else {
                    const errorMessage = result?.data?.createTaskComment?.errors?.[0]?.messages?.[0] || "Unknown Error"
                    throw new Error(errorMessage)
                }
            }).catch((error) => {
                setError(error?.message || error?.toString() || "Error creating task update")
                setLoading(false)
            })
        } catch (error) {
            console.log("Error creating task update", error)
            setError(error?.message || error?.toString() || "Error creating task update")
            setLoading(false)
        }
    }

    const formContent = (
        <Form className={`NewCommentForm ${parentCommentId ? "NewCommentForm--reply" : ""}`} name="NewCommentForm" onSubmit={createTaskComment}>
            {parentCommentId && (
                <div className="comment-reply-context">
                    <span>Replying in thread</span>
                    {initialMentionUser && <span className="comment-reply-context__user">to {getCommentAuthorName(initialMentionUser)}</span>}
                </div>
            )}
            <FormGroup className="NewCommentTextAreaFormGroup">
                {/* <Label for="exampleText">Write an update</Label> */}
                <div className="MentionsInputContainer position-relative">
                    <MentionsInput placeholder="Write an update and mention someone with @" value={commentBody} onChange={(e) => setCommentBody(e.target.value)} onPaste={handlePaste} className="text-area" inputRef={inp}>
                        <Mention
                            trigger="@"
                            data={(search, callBack) => searchRepsWithClient(props.apolloClient, search, callBack, { boardUsers })}
                            displayTransform={(id, display) => `@${display}`}
                            appendSpaceOnAdd={true}
                            renderSuggestion={(suggestion, search, highlightedDisplay) => renderMentionSuggestion(suggestion, highlightedDisplay)}
                        />
                    </MentionsInput>

                    <button type="button" className="pin-icon" title="Attach a file" aria-label="Attach a file" onClick={() => fileInputRef.current?.click()}><i className="fa fa-paperclip" aria-hidden="true"></i></button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        style={{ display: "none" }}
                        multiple
                        onChange={(e) => {
                            const selectedFiles = Array.from(e.target.files || [])
                            setFiles((prev) => [...prev, ...selectedFiles])
                            e.target.value = ""
                        }}
                    />
                </div>

            </FormGroup>
            {error && <div className="text-danger mb-2">{error}</div>}
            <div>
                {renderPendingAttachmentPreviews(files, removeFileAtIndex)}
            </div>
            <div className="update-submit-button-container">
                {onCancelReply && (
                    <Button type="button" color="link" className="comment-reply-cancel" onClick={onCancelReply}>
                        Cancel
                    </Button>
                )}
                <Button type="submit" disabled={!commentBody || loading || !commentId}>{loading ? `${submitLabel}...` : submitLabel}</Button>
            </div>
        </Form>
    )

    if (parentCommentId) {
        return (
            <div className="reply-composer">
                <div className="reply-composer__avatar">
                    <RepAvatar rep={currentUser} keepClose />
                </div>
                <div className="reply-composer__form">
                    {formContent}
                </div>
            </div>
        )
    }

    return (
        formContent
    )
}

export default withApolloClient(TaskDetailsSidePanel)
